import React, { createContext, useContext, useEffect, useReducer, useCallback, useMemo, ReactNode, useRef } from 'react';
import { debounce } from 'lodash';
import { ADHDTodoManager } from '../lib/adhd-todo-manager';
import { useAPIClient } from '../lib/api-client';
import { useAuth } from '@clerk/clerk-react';

interface UsageState {
  taskCount: number;
  projectCount: number;
  lastUpdated: string;
}

interface UsageAction {
  type: 'UPDATE_COUNTS' | 'RESET';
  payload?: Partial<UsageState>;
}

interface UsageTrackingContextType {
  taskCount: number;
  projectCount: number;
  lastUpdated: string;
  refreshCounts: () => void;
}

const initialState: UsageState = {
  taskCount: 0,
  projectCount: 0,
  lastUpdated: new Date().toISOString()
};

function usageReducer(state: UsageState, action: UsageAction): UsageState {
  switch (action.type) {
    case 'UPDATE_COUNTS':
      return {
        ...state,
        ...action.payload,
        lastUpdated: new Date().toISOString()
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

const UsageTrackingContext = createContext<UsageTrackingContextType | undefined>(undefined);

export interface UsageTrackingProviderProps {
  children: ReactNode;
}

export const UsageTrackingProvider: React.FC<UsageTrackingProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(usageReducer, initialState);
  const todoManager = useMemo(() => new ADHDTodoManager(), []);
  const apiClient = useAPIClient();
  const { isSignedIn } = useAuth();

  // Internal function to perform the actual update
  const performUpdate = useCallback(async () => {
    if (!isSignedIn) {
      // Fall back to localStorage if not signed in
      const todos = todoManager.getTodos();
      const projects = todoManager.getProjects();
      
      dispatch({
        type: 'UPDATE_COUNTS',
        payload: {
          taskCount: todos.length,
          projectCount: projects.length
        }
      });
      return;
    }

    try {
      // Fetch actual counts from the database via API using the combined endpoint
      const response = await apiClient.getUsageCounts();

      if (response.success) {
        const data = response.data as { actual_counts: { todos: number; projects: number } };
        
        dispatch({
          type: 'UPDATE_COUNTS',
          payload: {
            taskCount: data.actual_counts.todos,
            projectCount: data.actual_counts.projects
          }
        });
      } else {
        console.warn('Failed to fetch usage counts from API, falling back to localStorage');
        // Fall back to localStorage counts
        const todos = todoManager.getTodos();
        const projects = todoManager.getProjects();
        
        dispatch({
          type: 'UPDATE_COUNTS',
          payload: {
            taskCount: todos.length,
            projectCount: projects.length
          }
        });
      }
    } catch (error) {
      console.error('Error fetching usage counts:', error);
      // Fall back to localStorage counts
      const todos = todoManager.getTodos();
      const projects = todoManager.getProjects();
      
      dispatch({
        type: 'UPDATE_COUNTS',
        payload: {
          taskCount: todos.length,
          projectCount: projects.length
        }
      });
    }
  }, [todoManager, apiClient, isSignedIn]);

  // Create a debounced version of the update function (500ms delay)
  const debouncedUpdateCounts = useMemo(
    () => debounce(performUpdate, 500),
    [performUpdate]
  );

  // Keep reference to debounced function for cleanup
  const debouncedUpdateRef = useRef(debouncedUpdateCounts);
  debouncedUpdateRef.current = debouncedUpdateCounts;

  const refreshCounts = useCallback(() => {
    debouncedUpdateRef.current();
  }, []);

  // Update counts on mount (immediate, not debounced)
  useEffect(() => {
    performUpdate();
  }, [performUpdate]);

  // Listen for localStorage changes to automatically update counts (debounced)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'adhd-todos' || e.key === 'adhd-projects') {
        debouncedUpdateRef.current();
      }
    };

    // Listen for custom events when localStorage is modified by our app
    const handleCustomStorageChange = () => {
      debouncedUpdateRef.current();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adhdStorageUpdate', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adhdStorageUpdate', handleCustomStorageChange);
    };
  }, []);

  // Poll for changes every 30 seconds for real-time updates (reduced frequency)
  useEffect(() => {
    const interval = setInterval(() => {
      debouncedUpdateRef.current();
    }, 30000); // Increased from 10s to 30s to reduce API calls
    return () => clearInterval(interval);
  }, []);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateRef.current.cancel();
    };
  }, []);

  const contextValue: UsageTrackingContextType = {
    taskCount: state.taskCount,
    projectCount: state.projectCount,
    lastUpdated: state.lastUpdated,
    refreshCounts
  };

  return (
    <UsageTrackingContext.Provider value={contextValue}>
      {children}
    </UsageTrackingContext.Provider>
  );
};

export const useUsageTracking = () => {
  const context = useContext(UsageTrackingContext);
  if (!context) {
    throw new Error('useUsageTracking must be used within a UsageTrackingProvider');
  }
  return context;
};